import { api } from '../api';
import { withRetryAfter } from '../../utils/loadError';
import type { InterviewPrepRequest } from './interviewPrepApi';
import type { LanguageLearningRequest } from './languageLearningApi';
import type { SongPracticeRequest } from './songLearningApi';

/** Honest assessment projection. An unavailable dimension carries NO value. */
export interface ConversationResult {
  status: 'evaluated' | 'not_evaluated';
  sessionId?: string;
  /** Absent entirely when no defensible overall exists. */
  overall?: number;
  overallAvailable?: boolean;
  scored?: Array<{
    key: string;
    value: number;
    quality: number;
    evidence: Array<{ turnId?: string; charStart?: number; charEnd?: number }>;
  }>;
  notMeasured?: Array<{ key: string; reason: string; explanation: string }>;
  coverage?: { scoredCount: number; notMeasuredCount: number };
  modalitiesAssessed?: string[];
  evaluatedAt?: string;
  version?: { scoring: string | null; profile: string | null };
  /** The rubric the overall is computed from, and what it judges. */
  rubric?: {
    id: string;
    version: string;
    title: string;
    /** interview_practice: practice feedback on interview answers, not a hiring signal. */
    scope: 'general_communication' | 'scenario' | 'interview_practice' | string;
    overallUnavailableReason?: string;
    minScoredForOverall?: number;
    /** The rubric's own dimension labels, in rubric order. */
    dimensions?: Array<{ key: string; label: string }>;
  };
  /**
   * Where the rubric came from: the scenario's author, the flow (language /
   * interview practice), or — only when nothing was authored or mandated —
   * the modality's general rubric. Absent for older attempts.
   */
  rubricBasis?: 'authored' | 'flow' | 'general';
  /** The modality frozen into the attempt's binding. */
  modality?: ConversationModality;
  /** Video attempts only (P5): observable video evidence, each value classed. */
  videoEvidence?: VideoEvidenceValue[];
  /** The language the attempt was conducted and judged in (P5). */
  language?: PracticeLanguageView;
}

/** An attempt's frozen practice language, as the server states it. */
export interface PracticeLanguageView {
  code: string;
  label: string;
}

/**
 * One observable video value. A value exists ONLY when it was measured or
 * derived; an unavailable one has no number to render.
 */
export type VideoEvidenceValue =
  | {
      key: string;
      label: string;
      state: 'available';
      evidenceClass: 'MEASURED' | 'DERIVED';
      value: number;
      unit: 'ratio' | 'per_minute' | 'seconds';
      producer: string;
    }
  | {
      key: string;
      label: string;
      state: 'unavailable';
      evidenceClass: 'UNAVAILABLE';
      reason: string;
      explanation: string;
    };


// Types
export type MessageRole = 'user' | 'coach' | 'system';
/**
 * Legacy rows: pending | active | paused | completed | cancelled.
 * Canonical sessions report their own lifecycle as stored:
 * created | active | processing | completed | failed | cancelled.
 */
export type SessionStatus =
  | 'pending' | 'paused'
  | 'created' | 'processing' | 'failed'
  | 'active' | 'completed' | 'cancelled';

export interface CoachingMessage {
  id: string;
  sessionId: string;
  role: MessageRole;
  content: string;
  turnNumber: number;
  timestamp: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CoachingSession {
  id: string;
  userId: string;
  scenarioId?: string;
  voiceAvatarId?: string;
  status: SessionStatus;
  persona: string;
  startedAt?: string;
  endedAt?: string;
  durationSeconds: number;
  turnCount: number;
  metrics: Record<string, unknown>;
  feedback?: string;
  rating?: number;
  messages?: CoachingMessage[];
  /** Canonical sessions: the modality frozen into the attempt's binding. */
  modality?: ConversationModality;
  /** Canonical sessions: the practice language frozen into the binding (P5). */
  language?: PracticeLanguageView;
  /** Canonical sessions: the rubric the attempt will be assessed against. */
  rubric?: { id: string; version: string };
  /** Canonical sessions: Development or Testing, and what its frozen policy allows. */
  practice?: PracticeInfo;
  createdAt: string;
  updatedAt: string;
}

/**
 * One item of GET /coaching/sessions while the conversation engine is on: the
 * caller's history across both stores, each item stating which it came from.
 * A legacy row is read-only history (written text, no mode, never scored).
 */
export interface CoachingHistoryItem {
  id: string;
  source: 'canonical' | 'legacy';
  status: SessionStatus;
  persona: string;
  scenarioId: string | null;
  scenarioKey: string | null;
  modality: ConversationModality | null;
  mode: PracticeMode | null;
  turnCount: number;
  durationSeconds: number;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** A result exists for GET /coaching/sessions/:id/result. */
  resultAvailable: boolean;
}

/** Development (coaching, hints, retries) or Testing (a graded attempt). */
export type PracticeMode = 'development' | 'testing';

export interface PracticeInfo {
  mode: PracticeMode;
  hints: { used: number; allowed: number } | null;
  retriesAllowed: number;
}

export interface HintResponse {
  text: string;
  hintsUsed: number;
  hintsAllowed: number;
}

/**
 * The modality an attempt is conducted in. Chosen by committing to it — the
 * first written Send, or Start Live Conversation — and frozen for the attempt.
 */
export type ConversationModality = 'text' | 'realtime_audio' | 'realtime_audio_video';

export interface CreateSessionRequest {
  scenarioId?: string;
  scenarioName?: string;
  persona: string;
  voiceAvatarId?: string;
  modality?: ConversationModality;
  /** Development is the default; Testing needs an authored scenario. */
  mode?: PracticeMode;
  /**
   * Interview Prep: the learner's format and own context. The server chooses
   * the authored interview scenario; never sent together with scenarioId.
   */
  interviewPrep?: InterviewPrepRequest;
  /**
   * Language Learning: the learner's language profile (self-reported level,
   * region, register, situation, optional goal). Sent with the catalog
   * scenarioId; the server validates both and freezes the profile.
   */
  languageLearning?: LanguageLearningRequest;
  /**
   * Song learning: the piece and section to speak along with. The server
   * supplies the words through its rights gate; never sent with scenarioId.
   */
  songPractice?: SongPracticeRequest;
  /** BCP-47 practice language (P5). Absent means the base language. */
  language?: string;
  /** Development only: explicitly opt in to the language-practice rubric. */
  languageAssessment?: boolean;
  /** An NPC ladder level; the server checks it belongs to the persona and is unlocked. */
  npcLevelId?: string;
}

/** The level outcome of an NPC-level attempt, as recorded on finalize. */
export interface LevelAttemptResult {
  levelId: string;
  passed: boolean;
  newlyPassed: boolean;
  bestScoreImproved: boolean;
  currentBestScore: number;
  requiredScore: number;
  xpAwarded: number;
  scoresMet?: Record<string, boolean>;
}

/** The overall was not known, so the level could not be judged — not a fail, not a 0. */
export interface LevelNotEvaluable {
  levelId: string;
  notEvaluable: true;
  reason: 'overall_unknown' | string;
}

export type LevelOutcome = LevelAttemptResult | LevelNotEvaluable;

export interface FinalizeResult {
  resultId: string;
  level?: LevelOutcome;
}

/** Unwrap `{ success, data }`; tolerate an already-unwrapped body. */
function unwrapData<T>(response: unknown): T {
  const r = response as { data?: T } | null;
  return (r && typeof r === 'object' && 'data' in r ? r.data : response) as T;
}

export interface AddMessageRequest {
  content: string;
}

export interface AddMessageResponse {
  userMessage: CoachingMessage;
  coachMessage: CoachingMessage;
  turnNumber: number;
}

export interface SubmitFeedbackRequest {
  rating: number;
  feedback?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const coachingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // List coaching sessions
    listCoachingSessions: builder.query<
      PaginatedResponse<CoachingHistoryItem>,
      { page?: number; limit?: number; status?: string }
    >({
      query: (params) => ({
        url: '/coaching/sessions',
        params,
      }),
      transformResponse: (response: ApiResponse<CoachingHistoryItem[]> & { meta?: any }) => ({
        data: response.data,
        page: response.meta?.page || 1,
        limit: response.meta?.limit || 20,
        total: response.meta?.total || 0,
        totalPages: response.meta?.totalPages || 1,
      }),
      providesTags: ['Sessions'],
    }),

    // Get a single coaching session with messages
    getCoachingSession: builder.query<CoachingSession, string>({
      query: (id) => `/coaching/sessions/${id}`,
      transformResponse: (response: ApiResponse<CoachingSession>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Sessions', id }],
    }),

    // Create a new coaching session
    createCoachingSession: builder.mutation<CoachingSession, CreateSessionRequest>({
      query: (body) => ({
        url: '/coaching/sessions',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<CoachingSession>) => response.data,
      invalidatesTags: ['Sessions'],
    }),

    // Development only: a hint, limited by the attempt's frozen policy.
    requestCoachingHint: builder.mutation<HintResponse, string>({
      query: (id) => ({ url: `/coaching/sessions/${id}/hint`, method: 'POST' }),
      transformResponse: (response: ApiResponse<HintResponse>) => response.data,
    }),

    // Development only: a fresh attempt at the same practice.
    // A Development retry, created when the learner COMMITS it (first Send /
    // Start) in the mode they chose; the server applies the retry rules.
    retryCoachingSession: builder.mutation<CoachingSession, { id: string; modality: ConversationModality }>({
      query: ({ id, modality }) => ({ url: `/coaching/sessions/${id}/retry`, method: 'POST', body: { modality } }),
      transformResponse: (response: ApiResponse<CoachingSession>) => response.data,
      invalidatesTags: ['Sessions'],
    }),

    // Start a coaching session
    startCoachingSession: builder.mutation<CoachingSession, string>({
      query: (id) => ({
        url: `/coaching/sessions/${id}/start`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<CoachingSession>) => response.data,
      invalidatesTags: (_result, _error, id) => [{ type: 'Sessions', id }],
    }),

    // End a coaching session
    endCoachingSession: builder.mutation<CoachingSession, string>({
      query: (id) => ({
        url: `/coaching/sessions/${id}/end`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<CoachingSession>) => response.data,
      invalidatesTags: (_result, _error, id) => ['Sessions', { type: 'Sessions', id }],
    }),

    // Pause a coaching session
    pauseCoachingSession: builder.mutation<CoachingSession, string>({
      query: (id) => ({
        url: `/coaching/sessions/${id}/pause`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<CoachingSession>) => response.data,
      invalidatesTags: (_result, _error, id) => [{ type: 'Sessions', id }],
    }),

    // Resume a coaching session
    resumeCoachingSession: builder.mutation<CoachingSession, string>({
      query: (id) => ({
        url: `/coaching/sessions/${id}/resume`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<CoachingSession>) => response.data,
      invalidatesTags: (_result, _error, id) => [{ type: 'Sessions', id }],
    }),

    // Get messages for a session
    getCoachingMessages: builder.query<CoachingMessage[], string>({
      query: (id) => `/coaching/sessions/${id}/messages`,
      transformResponse: (response: ApiResponse<CoachingMessage[]>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Sessions', id }],
    }),

    // Add a message to a session
    addCoachingMessage: builder.mutation<AddMessageResponse, { id: string; content: string }>({
      query: ({ id, content }) => ({
        url: `/coaching/sessions/${id}/messages`,
        method: 'POST',
        body: { content },
      }),
      transformResponse: (response: ApiResponse<AddMessageResponse>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Sessions', id }],
    }),

    // Evaluate a finished conversation. An NPC-level attempt also reports its
    // level outcome, so the ladder (personas, levels, progress) is refetched.
    finalizeCoachingSession: builder.mutation<FinalizeResult, string>({
      query: (id) => ({
        url: `/coaching/sessions/${id}/finalize`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => unwrapData<FinalizeResult>(response),
      invalidatesTags: ['NpcProgress', { type: 'NpcPersonas', id: 'LIST' }],
    }),

    // The server wraps every body in { success, data }. Without unwrapping, the
    // results view read `status` off the envelope and never showed a result.
    getCoachingResult: builder.query<ConversationResult, string>({
      query: (id) => `/coaching/sessions/${id}/result`,
      transformResponse: (response: unknown) => unwrapData<ConversationResult>(response),
      // /coaching/:sessionId tells "busy" (429, Retry-After) from "not found" from "failed".
      transformErrorResponse: (error, meta) => withRetryAfter(error, meta),
    }),

    submitCoachingFeedback: builder.mutation<
      CoachingSession,
      { id: string; data: SubmitFeedbackRequest }
    >({
      query: ({ id, data }) => ({
        url: `/coaching/sessions/${id}/feedback`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<CoachingSession>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Sessions', id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListCoachingSessionsQuery,
  useGetCoachingSessionQuery,
  useCreateCoachingSessionMutation,
  useStartCoachingSessionMutation,
  useEndCoachingSessionMutation,
  usePauseCoachingSessionMutation,
  useResumeCoachingSessionMutation,
  useGetCoachingMessagesQuery,
  useAddCoachingMessageMutation,
  useSubmitCoachingFeedbackMutation,
  useFinalizeCoachingSessionMutation,
  useGetCoachingResultQuery,
  useRequestCoachingHintMutation,
  useRetryCoachingSessionMutation,
} = coachingApi;
